import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconInfoCircle, IconPlus } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import {
  getReflexAdminErrorMessage,
  ReflexDefinition,
  SaveReflexInput,
} from "../../../services/admin/reflexes.service";
import { scrollToFirstError } from "../../../utils/formValidation";
import { ReflexRuleEditor } from "./ReflexRuleEditor";
import {
  EMPTY_REFLEX_RULE,
  getReflexFormValues,
  ReflexFormValues,
  ReflexRuleFormValue,
  toSaveReflexInput,
} from "./reflexForm";

interface SelectOption {
  value: string;
  label: string;
}

interface ReflexFormModalProps {
  opened: boolean;
  reflex?: ReflexDefinition;
  panelOptions: SelectOption[];
  icdOptions: SelectOption[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (input: SaveReflexInput) => Promise<void>;
}

function validateName(value: string) {
  if (!value.trim()) return "Reflex name is required";
  return value.trim().length <= 120
    ? null
    : "Reflex name must be 120 characters or fewer";
}

function validateTarget(value: string, values: ReflexFormValues) {
  if (!value) return "Select a follow-on panel";
  if (value === values.startingPanelDefinitionId)
    return "The starting panel cannot be a follow-on panel";
  return values.rules.filter((rule) => rule.targetPanelDefinitionId === value)
    .length > 1
    ? "Each follow-on panel can appear only once"
    : null;
}

function validateCodes(value: string[]) {
  return value.length > 0 ? null : "Select at least one ICD-10 code";
}

export function ReflexFormModal({
  opened,
  reflex,
  panelOptions,
  icdOptions,
  isSubmitting,
  onClose,
  onSubmit,
}: ReflexFormModalProps) {
  const [discardRequested, setDiscardRequested] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const form = useForm<ReflexFormValues>({
    initialValues: getReflexFormValues(reflex),
    validateInputOnBlur: true,
    validate: {
      name: validateName,
      description: (value) =>
        value.trim().length <= 1000
          ? null
          : "Description must be 1000 characters or fewer",
      startingPanelDefinitionId: (value) =>
        value ? null : "Select a starting panel",
      rules: {
        targetPanelDefinitionId: validateTarget,
        icdCodeIds: validateCodes,
      },
    },
  });
  const title = useMemo(
    () =>
      reflex ? `Create version ${reflex.versionNumber + 1}` : "Create reflex",
    [reflex]
  );
  const handleRuleChange = useCallback(
    (index: number, value: ReflexRuleFormValue) =>
      form.setFieldValue(`rules.${index}`, value),
    [form]
  );
  const handleRuleRemove = useCallback(
    (index: number) => form.removeListItem("rules", index),
    [form]
  );
  const handleRuleMove = useCallback(
    (index: number, direction: -1 | 1) =>
      form.reorderListItem("rules", { from: index, to: index + direction }),
    [form]
  );
  const handleAddRule = useCallback(
    () =>
      form.insertListItem("rules", { ...EMPTY_REFLEX_RULE, icdCodeIds: [] }),
    [form]
  );
  const handleInvalid = useCallback(() => scrollToFirstError(form), [form]);
  const handleValid = useCallback(
    async (values: ReflexFormValues) => {
      setSubmitError("");
      try {
        await onSubmit(toSaveReflexInput(values));
      } catch (error) {
        setSubmitError(getReflexAdminErrorMessage(error));
      }
    },
    [onSubmit]
  );
  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) =>
      form.onSubmit(handleValid, handleInvalid)(event),
    [form, handleInvalid, handleValid]
  );
  const handleClose = useCallback(() => {
    if (form.isDirty()) setDiscardRequested(true);
    else onClose();
  }, [form, onClose]);
  const handleKeepEditing = useCallback(() => setDiscardRequested(false), []);
  const handleDiscard = useCallback(() => {
    setDiscardRequested(false);
    onClose();
  }, [onClose]);

  return (
    <Modal.Root
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside={false}
      closeOnEscape={!isSubmitting}
      size="xl"
      centered
    >
      <Modal.Overlay />
      <Modal.Content
        style={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}
      >
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
          <Modal.CloseButton disabled={isSubmitting} />
        </Modal.Header>
        <Modal.Body style={{ overflowY: "auto" }}>
          <Box
            component="form"
            id="reflex-form"
            noValidate
            onSubmit={handleSubmit}
          >
            <Stack gap="lg">
              {submitError && (
                <Alert
                  icon={<IconAlertCircle size={18} />}
                  title="Reflex could not be saved"
                  color="red"
                  role="alert"
                >
                  {submitError}
                </Alert>
              )}
              {reflex && (
                <Alert
                  icon={<IconInfoCircle size={18} />}
                  title="Versioned change"
                  color="blue"
                >
                  Accepted panel orders remain unchanged. Pending reflex
                  evaluations use the new rule version.
                </Alert>
              )}
              {discardRequested && (
                <Alert title="Discard unsaved changes?" color="orange">
                  <Group mt="sm">
                    <Button
                      size="xs"
                      variant="default"
                      onClick={handleKeepEditing}
                    >
                      Keep editing
                    </Button>
                    <Button size="xs" color="red" onClick={handleDiscard}>
                      Discard changes
                    </Button>
                  </Group>
                </Alert>
              )}
              <TextInput
                label="Reflex name"
                placeholder="e.g. Barrett tissue reflex"
                maxLength={120}
                withAsterisk
                {...form.getInputProps("name")}
              />
              <Textarea
                label="Description"
                placeholder="Explain when this reflex is ordered"
                minRows={2}
                maxLength={1000}
                {...form.getInputProps("description")}
              />
              <Select
                searchable
                label="Starting panel"
                description="This panel is ordered at accessioning when the reflex is selected."
                placeholder="Select an active panel"
                withAsterisk
                data={panelOptions}
                {...form.getInputProps("startingPanelDefinitionId")}
              />
              <Stack gap="sm">
                {form.values.rules.map((rule, index) => (
                  <ReflexRuleEditor
                    key={form.key(`rules.${index}`)}
                    index={index}
                    value={rule}
                    panelOptions={panelOptions}
                    icdOptions={icdOptions}
                    targetError={
                      form.errors[`rules.${index}.targetPanelDefinitionId`] as
                        | string
                        | undefined
                    }
                    icdError={
                      form.errors[`rules.${index}.icdCodeIds`] as
                        | string
                        | undefined
                    }
                    canRemove={form.values.rules.length > 1}
                    canMoveUp={index > 0}
                    canMoveDown={index < form.values.rules.length - 1}
                    onChange={handleRuleChange}
                    onRemove={handleRuleRemove}
                    onMove={handleRuleMove}
                  />
                ))}
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={handleAddRule}
                >
                  Add follow-on rule
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal.Body>
        <Group justify="flex-end" p="md">
          <Button
            variant="default"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" form="reflex-form" loading={isSubmitting}>
            {reflex ? "Create version" : "Create reflex"}
          </Button>
        </Group>
      </Modal.Content>
    </Modal.Root>
  );
}
